import React from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';

import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { Media } from '@/js/libs/ui';
import type { TableInfoProps } from '@/js/libs/ui/src/V2';
import { Table, Button, Icons, Filter } from '@/js/libs/ui/src/V2';

import type { SortProps } from 'src/hooks/useSortAndOrderState';
import { OrderDirection } from 'src/hooks/useSortAndOrderState';
import type { ColumnComponentPropsV2, ColumnV2, Option } from 'src/types';
import { SortingWrapper } from './styled';

export type SortableTableInfoProps<
  TColumnName extends string,
  TColumnProps extends ColumnComponentPropsV2<TColumnName> = ColumnComponentPropsV2<TColumnName>,
> = TableInfoProps & {
  columns: ColumnV2<TColumnName, TColumnProps>[];
  getSortProps: SortProps<TColumnName>;
};

export const SortableTableInfo = <
  TColumnName extends string,
  TColumnProps extends ColumnComponentPropsV2<TColumnName> = ColumnComponentPropsV2<TColumnName>,
>({
  columns,
  getSortProps,
  ...props
}: SortableTableInfoProps<TColumnName, TColumnProps>) => {
  const [t] = useTranslation(['backend', 'common']);
  const isMobile = useMediaQuery({ query: Media.xsMax });

  const selected = React.useMemo<Option<TColumnName> | null>(() => {
    const sorted = columns.find((column) => column.sortable && column.title && getSortProps(column.name).order);
    return sorted ? { label: sorted.title!, value: sorted.name } : null;
  }, [columns, getSortProps]);

  const options = React.useMemo<Option<TColumnName>[]>(
    () => columns.flatMap((column) => (column.sortable && column.title ? { label: column.title, value: column.name } : [])),
    [columns],
  );

  const handleClick = useCallbackRef(() => {
    if (selected) {
      getSortProps(selected.value).handleChangeOrder();
    }
  });

  const handleChange = useCallbackRef((option: Option<TColumnName> | null) => {
    if (option?.value) {
      getSortProps(option.value).handleChangeOrder();
    }
  });

  return (
    <Table.Info
      {...props}
      sorting={
        isMobile && (
          <SortingWrapper>
            <Button secondary iconOnly onClick={handleClick}>
              <Icons.Arrow
                align={selected?.value ? (getSortProps(selected.value).order === OrderDirection.ASC ? 'top' : 'bottom') : undefined}
              />
            </Button>
            <Filter selected={selected} onChange={handleChange} options={options} label={t('common:SORT_BY')} isClearable={false} />
          </SortingWrapper>
        )
      }
    />
  );
};
